import React from "react";
import _ from "lodash";
import queryString from "query-string";
import { NetworkAPI } from "apis";
import LoadingUI from "../../../common/LoadingUI";
import NetworkInfoHeader from "../../common/NetworkInfoHeader";
import DateTimeFormatter from "../../../common/DateTimeFormatter";
import { notify } from "react-notify-toast";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import CustomUPRSettingModal from "./CustomUPRSettingModal";
import BatchUpdateRulesModal from "./BatchUpdateRulesModal";
import BatchUpdateLabelModal from "./BatchUpdateLabelModal";
import CustomUPRJsonEditModal from "./CustomUPRJsonEditModal";
import CustomUPRDiffView from "./CustomUPRDiffView";
import UPRSettingsTable from "./NetworkUPRSettingsTable";
import EditCustomUPRDataModal from "./EditCustomUPRDataModal";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import PromptModal from "components/common/PromptModal";
import SimpleDataTable from "components/common/SimpleDataTable";
import ItemsFilter from "components/ops-mgmt/common/ItemsFilter";
import Checkbox from "components/common/Checkbox";
import ChangeLogModal from "components/common/ChangeLogModal";

import NotesInformation from "components/common/NotesInformation";
import PreviewValidateDiffModal from "components/common/PreviewValidateDiffModal";

import {
  compareTwoObjectsDiff,
  UprSettingSortedArray,
  UprSettingUniqIdMap,
  CustomGroupRule,
  CustomRuleAliasKey,
} from "helpers/Compare";
import { setQueryString } from "helpers/QueryString";
import { cleanupRule, groupLabels } from "helpers/CustomUprFirstLookUtils";
import { setValueByPath } from "helpers/Rollback";

import { MESSAGE } from "constants/Message";
import { CUSTOM_TYPE } from "./Constants";
import { MODAL_TYPE } from "../custom-first-look/Constants";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 whitespace-pre shadow hover:bg-gray-300 text-gray-900 font-semibold";

const buttonDisabledClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-200 whitespace-pre text-gray-400 font-semibold cursor-not-allowed";

class NetworkUPRSettingViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSaving: false,
      isLoading: false,

      networkId: null,
      networkInfo: null,
      errMsg: "",
      importIdList: [],

      units: [],
      virtualPlacements: [],
      currentRule: null,
      originalRules: null, // save original for diff
      customRules: null,
      batchRules: null,
      isDisabledHidden: true,
      placements: [],
      demandSource: null,

      labels: [],
      configsByLabel: [],
      searchByLabel: [],
      configsBySearch: [],
      isSearch: false,

      selectedIds: [],
      isUpdateLabelModalOpen: false,

      modalType: null, // "ADD", "EDIT"
      isModalOpen: false,
      formErrMsg: null,
      formWarnMsg: null,

      isEditing: false,
      saveMsg: null,

      isBatchUpdateModalOpen: false,
      isPromptModalOpen: false,
      promptHeader: null,
      promptMessage: null,
      promptAction: null,
      promptMsg: [],

      etag: null,
      notes: null,

      isChangeLogModalOpen: false,
      isEditDataModalOpen: false,
      isPreviewValidateModalOpen: false,
    };

    this.searchRef = React.createRef();

    this.handleCurrentRuleChanged = this.handleCurrentRuleChanged.bind(this);
    this.handleOpenEdit = this.handleOpenEdit.bind(this);
    this.handleOpenEditJSON = this.handleOpenEditJSON.bind(this);
    this.handleOpenAdd = this.handleOpenAdd.bind(this);
    this.handleOpenDuplicate = this.handleOpenDuplicate.bind(this);
    this.handleOpenChangeLog = this.handleOpenChangeLog.bind(this);

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleEditRuleJSON = this.handleEditRuleJSON.bind(this);
    this.handleEditRule = this.handleEditRule.bind(this);
    this.handleAddRule = this.handleAddRule.bind(this);
    this.handleRemoveRule = this.handleRemoveRule.bind(this);
    this.handleToggleEnabled = this.handleToggleEnabled.bind(this);
    this.handleToggleEnabledByLabel =
      this.handleToggleEnabledByLabel.bind(this);
    this.handleRemoveAllByLabel = this.handleRemoveAllByLabel.bind(this);
    this.handleReplaceCurrentConfig =
      this.handleReplaceCurrentConfig.bind(this);
    this.handleEditCurrentConfig = this.handleEditCurrentConfig.bind(this);
    this.handleGetRefData = this.handleGetRefData.bind(this);

    this.handleSaveCustomUprs = this.handleSaveCustomUprs.bind(this);
    this.handleReloadPage = this.handleReloadPage.bind(this);

    this.handleIsEditing = this.handleIsEditing.bind(this);
    this.handleOpenBatchUpdateModal =
      this.handleOpenBatchUpdateModal.bind(this);
    this.handleBatchUpdateRules = this.handleBatchUpdateRules.bind(this);

    this.handleSelect = this.handleSelect.bind(this);
    this.handleOpenUpdateLabelModal =
      this.handleOpenUpdateLabelModal.bind(this);
    this.handleSelectedRemove = this.handleSelectedRemove.bind(this);
    this.handleSelectedStatusChanged =
      this.handleSelectedStatusChanged.bind(this);
    this.handleBatchUpdateLabel = this.handleBatchUpdateLabel.bind(this);

    this.handleSearch = this.handleSearch.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);

    this.handleFilter = this.handleFilter.bind(this);

    this.handleFetchData = this.handleFetchData.bind(this);
    this.handleCompareData = this.handleCompareData.bind(this);

    this.handleRollBack = this.handleRollBack.bind(this);

    this.scrollToLabel = this.scrollToLabel.bind(this);
    this.toggleHideDisabled = this.toggleHideDisabled.bind(this);
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      const { networkId } = this.props.match.params;
      if (!networkId) {
        throw new Error("Missing Network ID");
      }
      const networkInfo = await NetworkAPI.getNetworkInfo({ networkId });
      if (!networkInfo) {
        throw new Error("Invalid Network");
      }

      const { placement } = await NetworkAPI.getNetworkResourceMapping({
        networkId,
      });

      const demandSource = await NetworkAPI.getNetworkDemandSources({
        networkId,
      });

      const units = await NetworkAPI.getUnitsByNetworkId({ networkId });
      const virtualPlacements = await NetworkAPI.getNetworkVirtualPlacements({
        networkId,
      });
      const { proposedAt, deployedAt, customSettings, etag, notes } =
        await NetworkAPI.getNetworkCustomUpr({ networkId });

      // New schema for v2: customs is an array of array instead of an array of objects
      // convert customs to v2
      _.forEach(customSettings, (c) => {
        if (c.targetingSection && c.targetingSection.customs) {
          if (!_.isArray(c.targetingSection.customs[0])) {
            c.targetingSection.customs = [c.targetingSection.customs];
          }
        }
      });

      // Has import querystring
      // const value = queryString.parse(this.props.location.search);
      const { importId } = queryString.parse(this.props.location.search);
      if (importId) {
        this.handleAutoImport({ importIdList: importId.split(",") });
      }

      this.setState(
        {
          networkId,
          networkInfo,
          originalRules: _.cloneDeep(customSettings),
          customRules: _.cloneDeep(customSettings),
          proposedAt,
          deployedAt,
          units,
          virtualPlacements,
          etag,
          notes,
          placements: placement ? placement.mapping : [],
          demandSource,

          isLoading: false,
        },
        () => {
          this.handleFilter({
            isDisabledHidden: this.state.isDisabledHidden,
            searchText: this.state.searchText,
          });
        }
      );
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isDisabledHidden !== this.state.isDisabledHidden) {
      this.handleFilter({
        isDisabledHidden: this.state.isDisabledHidden,
        searchText: this.state.searchText,
      });
    }
  }

  _searchConfig(inputValue, customSettings) {
    if (inputValue === "") return _.cloneDeep(customSettings);
    const filteredConfigs = _.filter(customSettings, (r) => {
      return r.name.toLowerCase().includes(inputValue.toLowerCase());
    });
    return filteredConfigs;
  }

  handleReloadPage() {
    this.setState({
      isPreviewValidateModalOpen: false,
    });
    notify.show("Rule updated! Reloading page...", "success");
    setTimeout(() => {
      window.location.reload(false);
    }, 3000);
  }

  async handleSaveCustomUprs({ notes, etag, configData, callback }) {
    const { networkId } = this.state;

    const hasCallback = !_.isNil(callback);
    this.setState({ isSaving: true, saveMsg: null });
    try {
      console.log("update upr", networkId, configData);

      const { result } = await NetworkAPI.updateNetworkCustomUpr({
        networkId,
        customSettings: configData,
        etag,
        notes,
      });
      console.log(result);
      if (result.op === "ok" || result.op === "OK") {
        this.setState({
          saveMsg: ["Update successful", MESSAGE.SUCCESS],
          originalRules: _.cloneDeep(configData), // Clear diffs to prevent beforeunload alert
        });
        if (hasCallback) {
          callback();
        } else {
          this.handleReloadPage();
        }
      } else {
        throw `Failed response: ${JSON.stringify(result)}`;
      }
    } catch (err) {
      this.setState({ saveMsg: [err.toString(), MESSAGE.ERROR] });
    } finally {
      this.setState({ isSaving: false });
    }
  }

  handleCurrentRuleChanged(newRule) {
    const cleanedRule = cleanupRule(newRule);
    this.setState({
      currentRule: cleanedRule,
    });
  }

  handleAutoImport({ importIdList }) {
    this.handleOpenAdd({ importIdList });
  }

  handleOpenAdd({ importIdList }) {
    const defaultRule = {
      enabled: true,
      targetingSection: {},
      priceSection: {
        priceType: 0,
      },
    };
    this.setState({
      isModalOpen: true,
      modalType: MODAL_TYPE.ADD,
      currentRule: defaultRule,
      importIdList,
    });
  }

  handleOpenEdit(rule) {
    this.setState({
      currentRule: rule,
      isModalOpen: true,
      modalType: MODAL_TYPE.EDIT,
    });
  }

  handleOpenEditJSON(rule) {
    this.setState({
      currentRule: rule,
      isModalOpen: true,
      modalType: MODAL_TYPE.JSON,
    });
  }

  handleOpenDuplicate(rule) {
    let duplicateRule = _.cloneDeep(rule);
    duplicateRule.id = "";
    duplicateRule.name = `${rule.name} copied`;

    this.setState({
      currentRule: duplicateRule,
      isModalOpen: true,
      modalType: MODAL_TYPE.COPY,
    });
  }

  handleOpenChangeLog(rule) {
    this.setState({
      currentRule: rule,
      isChangeLogModalOpen: true,
    });
  }

  async handleFetchData(param) {
    const { networkId } = this.state;
    return await NetworkAPI.getNetworkCustomUprChangeHistory({
      networkId,
      ...param,
    });
  }

  handleCompareData(from, to) {
    return compareTwoObjectsDiff(
      from,
      to,
      UprSettingUniqIdMap,
      UprSettingSortedArray
    );
  }

  handleCloseModal() {
    this.setState({
      isModalOpen: false,
      currentRule: null,
      modalType: null,
      formErrMsg: null,
      formWarnMsg: null,
      isEditing: false,
      isBatchUpdateModalOpen: false,
      isUpdateLabelModalOpen: false,
      importIdList: undefined,
    });
  }

  validate(ruleData) {
    const { modalType, currentRule, customRules } = this.state;
    const ruleIdList = customRules.map((r) => r.id);
    const newRule = ruleData || currentRule;
    const originalRuleId = !_.isNil(ruleData) ? currentRule.id : null;

    if (!newRule.id) {
      this.setState({
        formErrMsg: "ID is required",
      });
      return true;
    }

    if ([MODAL_TYPE.ADD, MODAL_TYPE.COPY].includes(modalType)) {
      if (ruleIdList.includes(_.toInteger(newRule.id))) {
        this.setState({
          formErrMsg: "ID must be unique",
        });
        return true;
      }
    } else {
      // Rule id changed in edit mode, need to check if new id is unique
      if (!_.isNil(originalRuleId) && originalRuleId !== newRule.id) {
        this.setState({
          formErrMsg: "ID cannot be changed in edit mode",
        });
        return true;
      }
    }

    if (!newRule.name) {
      this.setState({
        formErrMsg: "Name is required",
      });
      return true;
    }
    if (!newRule.priceSection) {
      this.setState({
        formErrMsg: "Pricing is required",
      });
      return true;
    } else {
      // LGO (2) and Use Brand Setting (-1) has no need for price
      if (
        newRule.priceSection.priceType !== 2 &&
        newRule.priceSection.priceType !== -1 &&
        (newRule.priceSection.price === null ||
          newRule.priceSection.price === undefined ||
          newRule.priceSection.price === "")
      ) {
        this.setState({
          formErrMsg: "Price is required",
        });
        return true;
      }
    }

    if (!newRule.targetingSection || _.isEmpty(newRule.targetingSection)) {
      this.setState({
        formErrMsg: "Targeting is required",
      });
      return true;
    }

    const unitIds = _.get(newRule, [
      "targetingSection",
      "inventory",
      "unitIds",
    ]);
    // const placementIds = _.get(newRule, [
    //   "targetingSection",
    //   "inventory",
    //   "placementIds",
    // ]);
    // const virtualPlacementIds = _.get(newRule, [
    //   "targetingSection",
    //   "inventory",
    //   "virtualPlacementIds",
    // ]);
    const excludedUnitIds = _.get(newRule, [
      "targetingSection",
      "inventory",
      "excludedUnitIds",
    ]);
    const hasUnitIds = unitIds && unitIds.length > 0;
    // const hasPlacementIds = placementIds && placementIds.length > 0;
    // const hasVirtualPlacementIds =
    // virtualPlacementIds && virtualPlacementIds.length > 0;
    const hasExcludeUnitIds = excludedUnitIds && excludedUnitIds.length > 0;

    // Inventory Targeting is not required 10/13/2023
    // if (
    //   !hasUnitIds &&
    //   !hasPlacementIds &&
    //   !hasVirtualPlacementIds &&
    //   !hasExcludeUnitIds
    // ) {
    //   this.setState({
    //     formErrMsg: "Targeting inventory is required",
    //   });

    //   hasError = true;
    //   return hasError;
    // }

    // unitIds and excludeUnitIds cannot include the same unit ids
    if (hasUnitIds && hasExcludeUnitIds) {
      const duplicateIds = _.intersection(unitIds, excludedUnitIds);
      if (duplicateIds.length > 0) {
        this.setState({
          formErrMsg: `Targeting units cannot be targeted in both Unit IDs and Exclude Unit IDs`,
        });

        return true;
      }
    }

    return false;
  }

  validateCustom(ruleData) {
    const custom = _.get(
      ruleData || this.state.currentRule,
      ["targetingSection", "customs"],
      []
    );
    return custom.length > 0;
  }

  handleEditRuleJSON(newRule) {
    if (_.isNil(newRule)) {
      this.setState({ formErrMsg: "Invalid JSON format" });
      return;
    }
    this.handleEditRule({ ruleData: newRule });
  }

  handleEditRule({ ruleData }) {
    if (this.state.isEditing) {
      return this.setState({
        formErrMsg: "Customs or Brand Price Settings is still editing",
      });
    }

    const hasError = this.validate(ruleData);
    if (hasError) return;

    if (!this.validateCustom(ruleData) && this.state.formWarnMsg === null) {
      return this.setState({
        formErrMsg: null,
        formWarnMsg: "Customs section is empty.",
      });
    }

    let { customRules } = this.state;

    const editedRule = ruleData || this.state.currentRule;

    if (editedRule.label === "") {
      editedRule.label = undefined;
    }

    const newRules = _.map(customRules, (r) => {
      // find the edited rule
      if (r.id === editedRule.id) {
        r = editedRule;
      }

      return r;
    });

    this.setState({
      isPromptModalOpen: true,
      promptHeader: "Edit rule",
      promptMsg: ["Edit Success", MESSAGE.SUCCESS],
      promptMessage: (
        <>
          <div className="font-semibold text-blue-600">
            {editedRule.id} {editedRule.name}
          </div>
        </>
      ),
    });

    this.setState(
      {
        customRules: newRules,
      },
      this._refreshFiltersCb
    );
    this.handleCloseModal();
  }

  handleAddRule() {
    if (this.state.isEditing) {
      return this.setState({
        formErrMsg: "Customs or Brand Price Settings is still editing",
      });
    }

    const hasError = this.validate();
    if (hasError) return;

    if (!this.validateCustom() && this.state.formWarnMsg === null) {
      return this.setState({
        formErrMsg: null,
        formWarnMsg: "Customs section is empty.",
      });
    }

    let { customRules } = this.state;
    const { importId } = this.state.currentRule;

    let newRule = importId
      ? _.omit(this.state.currentRule, ["importId"])
      : this.state.currentRule;

    if (newRule.label === "") {
      newRule.label = undefined;
    }

    newRule.id = _.parseInt(newRule.id);
    // console.log(customRules, newRule);

    let newRules = [...customRules, newRule];
    newRules = _.sortBy(newRules, ["id"]);

    const newImportIdList = _.filter(this.state.importIdList, (id) => {
      return id !== importId;
    });

    this.setState({
      isPromptModalOpen: true,
      promptHeader: "Add new rule",
      promptMsg: ["Add Success", MESSAGE.SUCCESS],
      promptMessage: (
        <>
          <div className="font-semibold text-blue-600">
            {newRule.id} {newRule.name}
          </div>
        </>
      ),
    });

    this.setState(
      {
        formErrMsg: null,
        formWarnMsg: null,
        customRules: newRules,
        importIdList: newImportIdList,
      },
      () => {
        this._refreshFiltersCb();

        // Reset query string when adding rule
        setQueryString(this.props, {
          importId: newImportIdList.length === 0 ? undefined : newImportIdList,
        });

        if (newImportIdList.length === 0) {
          this.handleCloseModal();
        }
      }
    );
  }

  handleRemoveRule(rule, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Remove Rule",
        promptMessage: (
          <>
            <div>Are you sure you want to remove rule?</div>
            <div className="font-semibold text-red-600">
              {rule.id} {rule.name}
            </div>
          </>
        ),
        promptAction: () => {
          this.handleRemoveRule(rule, false);
        },
      });
      return;
    }

    const { customRules, selectedIds } = this.state;
    const newRules = _.filter(customRules, (r) => {
      return r.id !== rule.id;
    });
    const newSelectedIds = _.filter(selectedIds, (id) => {
      return id !== rule.id;
    });

    this.setState(
      {
        customRules: newRules,
        selectedIds: newSelectedIds,
        promptMsg: ["Remove Success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleToggleEnabled(rule) {
    let { customRules } = this.state;
    const newRules = _.map(customRules, (r) => {
      if (r.id === rule.id) {
        return { ...r, enabled: !rule.enabled };
      }
      return r;
    });

    this.setState({ customRules: newRules }, this._refreshFiltersCb);
  }

  _refreshFiltersCb() {
    const { isDisabledHidden, searchText } = this.state;
    this.handleFilter({ isDisabledHidden, searchText });
  }

  handleToggleEnabledByLabel({ label, isEnable }, isPrompt = true) {
    const isNoLabel = label === "undefined";
    const { isSearch, searchByLabel, configsByLabel } = this.state;
    const searchList = _.map(searchByLabel[label], "id");
    const configList = _.map(configsByLabel[label], "id");

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: `${isEnable ? "Enable" : "Disable"} Rules`,
        promptMessage: (
          <>
            {isEnable ? "Enable" : "Disable"}{" "}
            {isSearch ? `${searchList.length}/${configList.length}` : "all"}{" "}
            rules in {isNoLabel ? "No label" : label}?
          </>
        ),
        promptAction: () => {
          this.handleToggleEnabledByLabel({ label, isEnable }, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    const { customRules } = this.state;
    const newRules = _.map(customRules, (r) => {
      if (searchList.indexOf(r.id) !== -1) {
        return { ...r, enabled: isEnable ? true : false };
      }

      return r;
    });

    this.setState({ customRules: newRules }, this._refreshFiltersCb);
  }

  handleRemoveAllByLabel({ label }, isPrompt = true) {
    const isNoLabel = label === "undefined";

    const {
      customRules,
      selectedIds,
      searchByLabel,
      configsByLabel,
      isSearch,
    } = this.state;
    const searchList = _.map(searchByLabel[label], "id");
    const configList = _.map(configsByLabel[label], "id");

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: `Remove ${isSearch ? "Searched" : "All"} Rules`,
        promptMessage: (
          <>
            Remove{" "}
            {isSearch
              ? `${searchList.length}/${configList.length}`
              : `all ${searchList.length}`}{" "}
            rules in {isNoLabel ? "No label" : label}?
          </>
        ),
        promptAction: () => {
          this.handleRemoveAllByLabel({ label }, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    const newRules = _.filter(customRules, (r) => {
      return searchList.indexOf(r.id) === -1;
    });

    const newSelectedIds = _.filter(selectedIds, (id) => {
      const item = _.find(newRules, (r) => {
        return r.id === id;
      });
      return item ? true : false;
    });

    this.setState(
      { customRules: newRules, selectedIds: newSelectedIds },
      this._refreshFiltersCb
    );
  }

  handleIsEditing(isEditing) {
    this.setState({ isEditing });
  }

  handleOpenBatchUpdateModal() {
    this.setState({ isBatchUpdateModalOpen: true });
  }

  handleBatchUpdateRules(ruleIds, { content, type }, isPrompt = true) {
    let { customRules } = this.state;

    const newRules = _.map(customRules, (r) => {
      if (_.indexOf(ruleIds, r.id) !== -1) {
        if (type === "country") {
          r.targetingSection = {
            ...r.targetingSection,
            [type]: content[type],
          };
        } else if (type === "label") {
          r.label = content.label;
        } else if (type === "customs") {
          const customs = r.targetingSection.customs || [];
          if (customs.length === 0) {
            customs.push([content.customs]);
          } else {
            _.forEach(customs, (list) => {
              const checkOpt = _.findIndex(
                list,
                (d) => d.keyId === content.customs.keyId
              );
              if (checkOpt < 0) {
                list.push(content.customs);
              }
            });
          }

          r.targetingSection.customs = customs;
        }
      }
      return r;
    });

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Batch update these rules",
        promptMessage: (
          <>
            {ruleIds.map((ruleId) => {
              const rule = _.find(customRules, { id: ruleId });
              return (
                <div key={ruleId} className="font-semibold text-blue-600">
                  {ruleId} {rule && rule.name}
                </div>
              );
            })}
          </>
        ),
        promptAction: () => {
          this.handleBatchUpdateRules(
            ruleIds,
            {
              content,
              type,
            },
            false
          );
        },
      });
      return;
    }

    this.setState(
      {
        customRules: newRules,
        isBatchUpdateModalOpen: false,
        promptMsg: ["Batch update success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleReplaceCurrentConfig(data, isPrompt = true) {
    const { customRules, currentRule, networkInfo } = this.state;
    const { config, historyId, createdAt, isSpecificRule } = data;

    let parsedConfig = config;
    if (isSpecificRule) {
      const changedRule = config[0];
      parsedConfig = _.isNil(changedRule)
        ? _.filter(customRules, (r) => r.id !== currentRule.id)
        : _.map(customRules, (r) => {
            if (r.id === changedRule.id) {
              return changedRule;
            }
            return r;
          });
    }

    const refData = this.handleGetRefData();

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Replace Current Config",
        promptMessage: (
          <>
            <div className="text-sm">The selected config is:</div>
            <div className="mb-1 flex items-center gap-1 font-semibold text-blue-600">
              {historyId}
              {" - "}
              <DateTimeFormatter
                datetime={createdAt}
                timezone={networkInfo.timezone}
                place="right"
              ></DateTimeFormatter>
            </div>
            {isSpecificRule && (
              <>
                <div className="text-sm">The selected rule is:</div>
                <div className="mb-1 flex items-center gap-1 font-semibold text-blue-600">
                  {currentRule.id} - {currentRule.name}
                </div>
              </>
            )}
            <div className="text-sm">
              And the following changes will be made to your current custom UPR.
            </div>
            <div className="bg-gray-100 px-2 py-1">
              <CustomUPRDiffView
                refData={refData}
                originalRules={customRules}
                newRules={parsedConfig}
                isInModal={true}
              ></CustomUPRDiffView>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleReplaceCurrentConfig(data, false);
        },
      });
      return;
    }

    this.setState(
      {
        currentRule: null,
        customRules: parsedConfig,
        isChangeLogModalOpen: false,
        promptMsg: ["Replace current config success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleEditCurrentConfig(config, isPrompt = true) {
    const { customRules } = this.state;

    const refData = this.handleGetRefData();
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Replace Current Config",
        promptMessage: (
          <>
            <div className="bg-gray-100 px-2 py-1">
              <CustomUPRDiffView
                refData={refData}
                originalRules={customRules}
                newRules={config}
                isInModal={true}
              ></CustomUPRDiffView>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleEditCurrentConfig(config, false);
        },
      });
      return;
    }

    this.setState(
      {
        customRules: config,
        isEditDataModalOpen: false,
        promptMsg: ["Edit current config success", MESSAGE.SUCCESS],
      },
      this._refreshFiltersCb
    );
  }

  handleSelect(selected) {
    this.setState({ selectedIds: selected });
  }

  handleOpenUpdateLabelModal() {
    this.setState({ isUpdateLabelModalOpen: true });
  }

  handleSelectedRemove(isPrompt = true) {
    const { selectedIds, customRules } = this.state;

    if (isPrompt) {
      const selectedList = _.map(selectedIds, (id) => {
        return (
          _.find(customRules, (r) => {
            return r.id === id;
          }) || {}
        );
      });
      this.setState({
        isPromptModalOpen: true,
        promptHeader: "Remove Selected Rules",
        promptMessage: (
          <>
            Are you sure you want to remove the {selectedList.length} selected
            rules? <br />
            <div
              className="my-2 overflow-y-auto"
              style={{ maxHeight: "20rem" }}
            >
              <SimpleDataTable
                items={selectedList}
                columns={[
                  { key: "id", label: "ID", className: "text-right" },
                  {
                    key: "name",
                    label: "Name",
                    className: "text-right",
                    style: { width: "70%" },
                  },
                ]}
              ></SimpleDataTable>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleSelectedRemove(false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    const newRules = _.filter(customRules, (r) => {
      return selectedIds.indexOf(r.id) === -1;
    });

    this.setState(
      { customRules: newRules, selectedIds: [] },
      this._refreshFiltersCb
    );
  }

  handleSelectedStatusChanged(status, isPrompt = true) {
    const { selectedIds, customRules } = this.state;

    if (isPrompt) {
      const selectedList = _.map(selectedIds, (id) => {
        return (
          _.find(customRules, (r) => {
            return r.id === id;
          }) || {}
        );
      });
      this.setState({
        isPromptModalOpen: true,
        promptHeader: `${status ? "Enable" : "Disable"} Selected Rules`,
        promptMessage: (
          <>
            Are you sure you want to {status ? "enable" : "disable"} the
            {` ${selectedList.length}`} selected rules? <br />
            <div
              className="my-2 overflow-y-auto"
              style={{ maxHeight: "20rem" }}
            >
              <SimpleDataTable
                items={selectedList}
                columns={[
                  { key: "id", label: "ID", className: "text-right" },
                  {
                    key: "name",
                    label: "Name",
                    className: "text-right",
                    style: { width: "70%" },
                  },
                ]}
              ></SimpleDataTable>
            </div>
          </>
        ),
        promptAction: () => {
          this.handleSelectedStatusChanged(status, false);
          this.setState({ isPromptModalOpen: false });
        },
      });
      return;
    }

    const newRules = _.map(customRules, (r) => {
      if (selectedIds.indexOf(r.id) >= 0) {
        return { ...r, enabled: status };
      }

      return r;
    });

    this.setState(
      { customRules: newRules, selectedIds: [] },
      this._refreshFiltersCb
    );
  }

  handleBatchUpdateLabel(label) {
    const { customRules, selectedIds } = this.state;

    const newRules = _.map(customRules, (r) => {
      // find the edited rule
      if (selectedIds.indexOf(r.id) !== -1) {
        return { ...r, label };
      }
      return r;
    });

    this.setState({ customRules: newRules }, this._refreshFiltersCb);
    this.handleCloseModal();
  }

  handleSearch({ value }) {
    const { customRules, isDisabledHidden } = this.state;
    let status = isDisabledHidden ? "ENABLED" : "ALL";
    let filteredRules = this._filterByStatus(status, customRules);
    this._handleSearch({
      value,
      settings: filteredRules,
    });
  }

  handleClearSearch() {
    if (this.searchRef.current) {
      this.searchRef.current.handleClearInput();
      this.handleSearch({ value: "" });
    }
  }

  handleFilter({ isDisabledHidden, searchText }) {
    const { customRules } = this.state;
    let status = isDisabledHidden ? "ENABLED" : "ALL";
    let filteredRules = this._filterByStatus(status, customRules);
    this._handleSearch({
      value: searchText,
      settings: filteredRules,
    });
  }

  handleRollBack({ path, from, to }) {
    if (_.isNil(path)) return;

    const { customRules, originalRules } = this.state;

    const newRule = setValueByPath({
      original: originalRules,
      changed: customRules,
      path,
      from,
      to,
    });
    this.setState(
      {
        customRules: newRule,
      },
      this._refreshFiltersCb
    );
  }

  handleGetRefData() {
    const { customRules, originalRules } = this.state;
    return _.unionBy(customRules || [], originalRules || [], "id");
  }

  _handleSearch({ value, settings }) {
    let inputValue = value;
    if (inputValue === undefined && this.searchRef.current) {
      inputValue = this.searchRef.current.state.inputValue;
    }

    let ruleSettings = settings || this.state.customRules;
    const result = this._searchConfig(inputValue, ruleSettings);

    const { labels, configsByLabel, searchByLabel } = groupLabels(
      ruleSettings,
      result,
      inputValue !== ""
    );

    this.setState({
      configsBySearch: result,
      isSearch: inputValue !== "",
      labels,
      configsByLabel,
      searchByLabel,
    });
  }

  _filterByStatus(status, customSettings) {
    if (status === "ALL") return _.cloneDeep(customSettings);
    return _.filter(customSettings, (r) => {
      return r.enabled === (status === "ENABLED" ? true : false);
    });
  }

  toggleHideDisabled() {
    this.setState({ isDisabledHidden: !this.state.isDisabledHidden });
  }

  scrollToLabel(label) {
    const element = document.getElementById(label);

    if (element) {
      window.scrollTo({
        behavior: "auto",
        top: element.offsetTop - 56,
      });
    }
  }

  render() {
    const {
      networkId,
      networkInfo,
      originalRules,
      customRules,
      proposedAt,
      deployedAt,
      notes,
      etag,

      currentRule,
      units,
      virtualPlacements,
      placements,
      demandSource,

      labels,
      configsByLabel,
      searchByLabel,
      configsBySearch,

      isDisabledHidden,

      isLoading,
      errMsg,

      isModalOpen,
      modalType,
      formErrMsg,
      formWarnMsg,
      isEditing,

      isSaving,
      saveMsg,

      isBatchUpdateModalOpen,
      isUpdateLabelModalOpen,
      isPromptModalOpen,
      isChangeLogModalOpen,
      isEditDataModalOpen,
      isPreviewValidateModalOpen,

      promptHeader,
      promptMessage,
      promptAction,
      promptMsg,

      importIdList,
      selectedIds,

      isSearch,
    } = this.state;

    const selectedRules = _.filter(customRules, (r) => {
      return selectedIds.indexOf(r.id) !== -1;
    });

    const allEnabledNum = _.filter(customRules, (r) => r.enabled).length;
    const allDisabledNum = _.filter(customRules, (r) => !r.enabled).length;

    const filteredEnabledNum = _.filter(
      configsBySearch,
      (r) => r.enabled
    ).length;
    const filteredDisabledNum = _.filter(
      configsBySearch,
      (r) => !r.enabled
    ).length;

    const refData = this.handleGetRefData();

    return (
      <div>
        {isLoading && <LoadingUI></LoadingUI>}
        {errMsg && <div className="text-red-800">{errMsg}</div>}

        {networkInfo && (
          <div>
            <NetworkInfoHeader networkInfo={networkInfo}></NetworkInfoHeader>

            <div className="min-h-screen bg-gray-200 px-12 py-8">
              <div className="flex w-3/4 items-center justify-between pr-2">
                <PageTitle title="Network Custom UPRs"></PageTitle>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={buttonActionClass}
                    onClick={() => this.setState({ isEditDataModalOpen: true })}
                  >
                    Edit Custom JSON
                  </button>
                  <button
                    type="button"
                    className={buttonActionClass}
                    onClick={() =>
                      this.setState({ isChangeLogModalOpen: true })
                    }
                  >
                    Change History
                  </button>
                </div>
              </div>

              <div className="flex w-full">
                <div className="my-2 w-3/4 pr-2">
                  <div className="text-sm font-medium">Notes</div>
                  <NotesInformation notes={notes} />

                  <div className="rounded mt-2 flex items-center justify-between bg-white p-4">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className={buttonActionClass}
                        onClick={this.handleOpenBatchUpdateModal}
                      >
                        Batch update rules
                      </button>
                      <ActionButtonList
                        selectedIds={selectedIds}
                        handleSelectedStatusChanged={(flag) =>
                          this.handleSelectedStatusChanged(flag)
                        }
                        handleSelectedRemove={this.handleSelectedRemove}
                      ></ActionButtonList>
                    </div>
                    <button
                      type="button"
                      className="shadow rounded bg-blue-200 px-4 py-2 font-medium text-blue-800 hover:bg-blue-300"
                      onClick={this.handleOpenAdd}
                    >
                      Add custom rule
                    </button>
                  </div>

                  <div className="mt-2 flex items-center gap-4 text-gray-800">
                    <div>
                      <span className="font-semibold">
                        Total: {customRules.length} rules
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="flex items-center gap-1 text-green-600">
                        <FiCheckCircle></FiCheckCircle> Enabled
                      </span>
                      : {allEnabledNum}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="flex items-center gap-1 text-red-600">
                        <FiXCircle></FiXCircle> Disabled
                      </span>
                      : {allDisabledNum}
                    </div>
                    <label className="rounded ml-4 flex cursor-pointer items-center py-2">
                      <Checkbox
                        isChecked={isDisabledHidden}
                        onChange={this.toggleHideDisabled}
                      ></Checkbox>
                      <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
                        Hide Disabled
                      </span>
                    </label>
                  </div>

                  <div style={{ marginBottom: "600px" }}>
                    <div className="mb-2 flex w-2/3 gap-2">
                      <div className="w-3/4">
                        <ItemsFilter
                          ref={this.searchRef}
                          handleSearch={(value) => this.handleSearch({ value })}
                          placeholder="Search by UPR Name"
                        ></ItemsFilter>
                      </div>
                      <div className="p-1">
                        <button
                          type="button"
                          className={buttonActionClass}
                          onClick={this.handleClearSearch}
                        >
                          Clear Search
                        </button>
                      </div>
                    </div>

                    <div className="mb-2 font-medium text-gray-900">
                      Showing {configsBySearch.length} out of{" "}
                      {customRules.length} rules ({filteredEnabledNum} enabled,{" "}
                      {filteredDisabledNum} disabled)
                    </div>

                    <div className="my-2 flex flex-wrap items-center gap-1 text-sm">
                      <div className="font-semibold text-gray-700">
                        Jump to:{" "}
                      </div>
                      {labels.map((label) => {
                        return (
                          <button
                            key={label.value}
                            type="button"
                            className="border rounded border-gray-200 bg-white px-2 py-1 text-gray-800 hover:border-blue-400"
                            onClick={() => {
                              this.scrollToLabel(label.value);
                            }}
                          >
                            {label.name}
                          </button>
                        );
                      })}
                    </div>

                    {labels.map((label) => {
                      return (
                        <div
                          id={label.value}
                          key={label.value}
                          className="border mb-8 border-gray-400"
                        >
                          <div className="flex items-center justify-between  bg-white p-4 text-xl font-semibold text-gray-800">
                            <div>
                              {label.value === "undefined"
                                ? "No label"
                                : label.value}
                            </div>

                            <div className="flex items-center gap-2">
                              <>
                                <div
                                  className="text-right"
                                  style={{ width: "6.5rem" }}
                                >
                                  {isSearch && (
                                    <span>
                                      <span className="text-gray-700 underline">
                                        {label.numOfItemsSearch}
                                      </span>{" "}
                                      /{" "}
                                    </span>
                                  )}
                                  {label.numOfItems}
                                </div>
                              </>
                            </div>
                          </div>

                          <UPRSettingsTable
                            networkId={networkId}
                            allItems={configsByLabel[label.value]}
                            isSearch={isSearch}
                            items={searchByLabel[label.value]}
                            networkInfo={networkInfo}
                            units={units}
                            virtualPlacements={virtualPlacements}
                            placements={placements}
                            demandSource={demandSource}
                            selectedItems={this.state.selectedIds}
                            handleOpenEdit={this.handleOpenEdit}
                            handleOpenEditJSON={this.handleOpenEditJSON}
                            handleOpenDuplicate={this.handleOpenDuplicate}
                            handleOpenChangeLog={this.handleOpenChangeLog}
                            handleRemoveRule={this.handleRemoveRule}
                            handleToggleEnabled={this.handleToggleEnabled}
                            handleSelect={this.handleSelect}
                          ></UPRSettingsTable>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="w-1/4 pl-2">
                  <div className="mb-4 text-sm font-semibold text-gray-700">
                    <div className="text-gray-600">Proposed at:</div>
                    <DateTimeFormatter
                      datetime={proposedAt}
                      timezone={networkInfo.timezone}
                    ></DateTimeFormatter>
                  </div>

                  <div className="text-sm font-semibold text-gray-700">
                    <div className="text-gray-600">Deployed at:</div>
                    <DateTimeFormatter
                      datetime={deployedAt}
                      timezone={networkInfo.timezone}
                    ></DateTimeFormatter>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      className="shadow rounded bg-blue-400 px-8 py-3 font-semibold text-white hover:bg-blue-600"
                      onClick={() =>
                        this.setState({ isPreviewValidateModalOpen: true })
                      }
                    >
                      Preview and Save
                    </button>
                  </div>

                  <div className="mt-4 py-4">
                    <CustomUPRDiffView
                      refData={refData}
                      originalRules={originalRules}
                      newRules={customRules}
                      handleRollBack={this.handleRollBack}
                    ></CustomUPRDiffView>
                  </div>
                </div>
                <SelectedStickyFooter
                  idList={["id"]}
                  selectedItems={selectedIds}
                  handleSelectedByKey={() => {}}
                  handleClear={() => this.handleSelect([])}
                  actionButtons={
                    <div className="flex gap-2">
                      <ActionButtonList
                        selectedIds={selectedIds}
                        handleSelectedStatusChanged={(flag) =>
                          this.handleSelectedStatusChanged(flag)
                        }
                        handleSelectedRemove={this.handleSelectedRemove}
                      ></ActionButtonList>
                    </div>
                  }
                ></SelectedStickyFooter>
              </div>

              {isModalOpen && modalType !== MODAL_TYPE.JSON && (
                <CustomUPRSettingModal
                  networkId={networkId}
                  networkInfo={networkInfo}
                  ruleList={customRules}
                  currentRule={currentRule}
                  placements={placements}
                  demandSource={demandSource}
                  isModalOpen={isModalOpen}
                  modalType={modalType}
                  importIdList={importIdList}
                  formErrMsg={formErrMsg}
                  formWarnMsg={formWarnMsg}
                  isEditing={isEditing}
                  isSaving={isSaving}
                  handleCloseModal={this.handleCloseModal}
                  handleIsEditing={this.handleIsEditing}
                  handleCurrentRuleChanged={this.handleCurrentRuleChanged}
                  handleAddRule={this.handleAddRule}
                  handleEditRule={this.handleEditRule}
                ></CustomUPRSettingModal>
              )}

              {isModalOpen && modalType === MODAL_TYPE.JSON && (
                <CustomUPRJsonEditModal
                  currentRule={currentRule}
                  formErrMsg={formErrMsg}
                  formWarnMsg={formWarnMsg}
                  handleCloseModal={this.handleCloseModal}
                  handleConfirm={this.handleEditRuleJSON}
                ></CustomUPRJsonEditModal>
              )}

              {isBatchUpdateModalOpen && (
                <BatchUpdateRulesModal
                  networkId={networkId}
                  networkInfo={networkInfo}
                  isModalOpen={isBatchUpdateModalOpen}
                  customRules={customRules}
                  selectedRules={selectedRules}
                  //
                  formErrMsg={formErrMsg}
                  isEditing={isEditing}
                  isSaving={isSaving}
                  handleCloseModal={this.handleCloseModal}
                  handleBatchUpdateRules={this.handleBatchUpdateRules}
                ></BatchUpdateRulesModal>
              )}

              {isUpdateLabelModalOpen && (
                <BatchUpdateLabelModal
                  rules={selectedRules}
                  networkInfo={networkInfo}
                  isModalOpen={isUpdateLabelModalOpen}
                  handleCloseModal={this.handleCloseModal}
                  handleConfirm={this.handleBatchUpdateLabel}
                ></BatchUpdateLabelModal>
              )}

              {isEditDataModalOpen && (
                <EditCustomUPRDataModal
                  networkId={networkId}
                  networkInfo={networkInfo}
                  units={units}
                  virtualPlacements={virtualPlacements}
                  placements={placements}
                  currentData={customRules}
                  handleClose={() =>
                    this.setState({ isEditDataModalOpen: false })
                  }
                  handleDataChanged={this.handleEditCurrentConfig}
                ></EditCustomUPRDataModal>
              )}

              {isPreviewValidateModalOpen && (
                <PreviewValidateDiffModal
                  type={CUSTOM_TYPE.UPR}
                  networkId={networkId}
                  name={networkInfo.name}
                  etag={etag}
                  originalRules={originalRules}
                  currentData={customRules}
                  hasEnsure={true}
                  isSaving={isSaving}
                  saveMsg={saveMsg}
                  refData={refData}
                  handleClose={() =>
                    this.setState({ isPreviewValidateModalOpen: false })
                  }
                  handleSave={(params) => {
                    this.handleSaveCustomUprs(params);
                  }}
                  handleReload={this.handleReloadPage}
                  handleResetMsg={() => this.setState({ saveMsg: null })}
                ></PreviewValidateDiffModal>
              )}

              {isPromptModalOpen && (
                <PromptModal
                  isOpenModal={isPromptModalOpen}
                  handleConfirm={promptAction}
                  handleCancel={() =>
                    this.setState({ isPromptModalOpen: false, promptMsg: [] })
                  }
                  msg={promptMsg}
                  header={promptHeader}
                  width={
                    promptHeader === "Replace Current Config"
                      ? "45%"
                      : undefined
                  }
                >
                  {promptMessage}
                </PromptModal>
              )}
            </div>
          </div>
        )}

        {isChangeLogModalOpen && (
          <ChangeLogModal
            networkId={networkId}
            currentRule={currentRule}
            name={networkInfo.name}
            timezone={networkInfo.timezone}
            dataKey="customSetting"
            refData={refData}
            groupRule={CustomGroupRule}
            aliasKey={CustomRuleAliasKey}
            fetchData={this.handleFetchData}
            compareData={this.handleCompareData}
            handleClose={() =>
              this.setState({ isChangeLogModalOpen: false, currentRule: null })
            }
            handleReplaceData={this.handleReplaceCurrentConfig}
          ></ChangeLogModal>
        )}
      </div>
    );
  }
}

class ActionButtonList extends React.PureComponent {
  render() {
    const { selectedIds, handleSelectedStatusChanged, handleSelectedRemove } =
      this.props;
    return (
      <>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={() => handleSelectedStatusChanged(true)}
        >
          Enable selected
        </button>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={() => handleSelectedStatusChanged(false)}
        >
          Disable selected
        </button>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          className={
            selectedIds.length > 0 ? buttonActionClass : buttonDisabledClass
          }
          onClick={handleSelectedRemove}
        >
          Remove selected
        </button>
      </>
    );
  }
}

function PageTitle({ title }) {
  return <div className="text-3xl font-extrabold">{title}</div>;
}

export default NetworkUPRSettingViewer;
